import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, ClipboardCheck, Clock, Copy, Download, ExternalLink, BookOpen, RotateCcw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { facultyExamApi } from '../../api/examApi';
import { DashboardLayout } from '../../components/layout';
import { parseApiError } from '../../lib/apiErrors';
import { checkPdfFile } from '../../lib/uploadLimits';
import UploadHint from '../../components/UploadHint';
import PdfExamViewer from '../../components/PdfExamViewer';
import OfflineEvaluationPanel from '../../components/faculty/OfflineEvaluationPanel';
import ExamAssignmentPicker from '../../components/faculty/ExamAssignmentPicker';

export default function ExamDetailPage() {
  const { examId } = useParams();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [metaSaving, setMetaSaving] = useState(false);
  const [submissionsToggling, setSubmissionsToggling] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    subject: '',
    duration_minutes: 60,
    total_marks: 100,
  });
  const [assignments, setAssignments] = useState([]);
  const [revoking, setRevoking] = useState(null);
  const [extendingId, setExtendingId] = useState(null);
  const [customMinutes, setCustomMinutes] = useState({});
  const [reopeningId, setReopeningId] = useState(null);
  const [reopenMinutes, setReopenMinutes] = useState({});

  // Question PDF — loaded once via authenticated endpoint into a blob URL
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfLoadError, setPdfLoadError] = useState(false);
  const blobUrlRef = useRef(null);

  // Revoke blob URL on unmount to free memory
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!examId || !exam?.pdf_url) {
      return;
    }
    let cancelled = false;
    setPdfLoadError(false);
    facultyExamApi.getQuestionPdf(examId)
      .then((res) => {
        if (cancelled) return;
        const blob = new Blob([res.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        if (blobUrlRef.current) {
          URL.revokeObjectURL(blobUrlRef.current);
        }
        blobUrlRef.current = url;
        setPdfBlobUrl(url);
      })
      .catch(() => {
        if (!cancelled) setPdfLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [examId, exam?.pdf_url]);

  useEffect(() => {
    facultyExamApi
      .getSubjects()
      .then((res) => setSubjects(res.data))
      .catch(() => toast.error('Failed to load subjects'));
  }, []);

  // The exam's current subject may not be in the canonical list (e.g. legacy
  // free-text data from before this was a select) — surface it as an extra
  // option so the dropdown doesn't silently blank out, but its unrecognized
  // label makes clear it must be replaced before saving will succeed.
  const subjectOptions = useMemo(() => {
    if (!formData.subject) return subjects;
    const isKnown = subjects.some((s) => s.toLowerCase() === formData.subject.toLowerCase());
    return isKnown ? subjects : [formData.subject, ...subjects];
  }, [subjects, formData.subject]);
  const subjectUnrecognized = formData.subject && !subjects.some((s) => s.toLowerCase() === formData.subject.toLowerCase());

  const publicExamUrl = useMemo(() => {
    if (!exam?.public_access_token) return '';
    return `${window.location.origin}/exam/${exam.public_access_token}`;
  }, [exam]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [examResponse, submissionsResponse, assignmentsResponse] = await Promise.all([
        facultyExamApi.getExam(examId),
        facultyExamApi.getExamSubmissions(examId),
        facultyExamApi.getAssignments(examId),
      ]);
      setExam(examResponse.data);
      setFormData({
        title: examResponse.data.title || '',
        subject: examResponse.data.subject || '',
        duration_minutes: examResponse.data.duration_minutes || 60,
        total_marks: examResponse.data.total_marks || 100,
        exam_date: examResponse.data.exam_date || '',
      });
      setSubmissions(submissionsResponse.data);
      setAssignments(assignmentsResponse.data);
    } catch (error) {
      toast.error(parseApiError(error, 'Failed to load exam').message);
      navigate('/faculty/exams');
    } finally {
      setLoading(false);
    }
  }, [examId, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handlePublishToggle = async () => {
    if (!exam) return;
    setSaving(true);
    try {
      const nextStatus = exam.is_published === 'published' ? 'draft' : 'published';
      const response = await facultyExamApi.updateExam(exam.id, { is_published: nextStatus });
      setExam(response.data);
      toast.success(nextStatus === 'published' ? 'Exam published' : 'Exam unpublished');
    } catch (error) {
      toast.error(parseApiError(error, 'Failed to update exam').message);
    } finally {
      setSaving(false);
    }
  };

  const handleMetaChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: ['title', 'subject', 'exam_date'].includes(name) ? value : Number(value) || 0,
    }));
  };

  const handleSaveExamDetails = async (event) => {
    event.preventDefault();
    setMetaSaving(true);
    try {
      // Send only the fields that apply to this exam's mode: an online exam has
      // no exam_date (formData holds '' for it, which the API rejects with a 422)
      // and a paper test has no duration.
      const payload = { ...formData };
      if (exam.conduct_mode === 'offline') {
        delete payload.duration_minutes;
      } else {
        delete payload.exam_date;
      }
      const response = await facultyExamApi.updateExam(exam.id, payload);
      if (pdfFile) {
        const uploadData = new FormData();
        uploadData.append('file', pdfFile);
        await facultyExamApi.uploadPdf(exam.id, uploadData);
        setPdfFile(null);
      }
      setExam(response.data);
      toast.success('Exam details updated');
      await loadData();
    } catch (error) {
      toast.error(parseApiError(error, 'Failed to update exam').message);
    } finally {
      setMetaSaving(false);
    }
  };

  const handleCopy = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error(`Could not copy ${label.toLowerCase()}`);
    }
  };

  const handleAuthDownload = async (downloadFn, filename) => {
    try {
      const response = await downloadFn();
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      toast.error(parseApiError(error, 'Download failed').message);
    }
  };

  const handleToggleSubmissions = async () => {
    if (!exam) return;
    setSubmissionsToggling(true);
    try {
      const nextState = !exam.submissions_open;
      const response = await facultyExamApi.toggleSubmissionsOpen(exam.id, nextState);
      setExam(response.data);
      toast.success(nextState ? 'Submissions reopened' : 'Submissions closed');
    } catch (error) {
      toast.error(parseApiError(error, 'Failed to update submission window').message);
    } finally {
      setSubmissionsToggling(false);
    }
  };

  const handleDeleteExam = async () => {
    const confirmed = window.confirm('Delete this exam and all its submissions? This cannot be undone.');
    if (!confirmed) return;
    try {
      await facultyExamApi.deleteExam(exam.id);
      toast.success('Exam deleted');
      navigate('/faculty/exams');
    } catch (error) {
      toast.error(parseApiError(error, 'Failed to delete exam').message);
    }
  };

  const assignedStudentIds = useMemo(() => new Set(assignments.map((a) => a.student_id)), [assignments]);

  const handleRevokeAssignment = async (assignment) => {
    if (assignment.started_at) return;
    const confirmed = window.confirm(`Remove ${assignment.student_name} from this exam's assignment list?`);
    if (!confirmed) return;
    setRevoking(assignment.id);
    try {
      await facultyExamApi.revokeAssignment(exam.id, assignment.id);
      toast.success('Assignment removed');
      await loadData();
    } catch (error) {
      toast.error(parseApiError(error, 'Failed to remove assignment').message);
    } finally {
      setRevoking(null);
    }
  };

  const handleExtend = async (assignment, minutes) => {
    if (!minutes || minutes <= 0) {
      toast.error('Enter a number of minutes greater than 0');
      return;
    }
    setExtendingId(assignment.id);
    try {
      await facultyExamApi.extendAssignment(exam.id, assignment.id, minutes);
      toast.success(`Extended by ${minutes} min`);
      setCustomMinutes((prev) => ({ ...prev, [assignment.id]: '' }));
      await loadData();
    } catch (error) {
      toast.error(parseApiError(error, 'Failed to extend deadline').message);
    } finally {
      setExtendingId(null);
    }
  };

  const handleReopen = async (assignment) => {
    const minutes = Number(reopenMinutes[assignment.id]) || 5;
    if (minutes <= 0) {
      toast.error('Enter a number of minutes greater than 0');
      return;
    }
    setReopeningId(assignment.id);
    try {
      await facultyExamApi.reopenAssignment(exam.id, assignment.id, minutes);
      toast.success(`Reopened — student can view and upload again for ${minutes} min`);
      setReopenMinutes((prev) => ({ ...prev, [assignment.id]: '' }));
      await loadData();
    } catch (error) {
      toast.error(parseApiError(error, 'Failed to reopen attempt').message);
    } finally {
      setReopeningId(null);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Exam Details">
        <div className="space-y-4 animate-pulse">
          <div className="h-24 rounded-lg bg-slate-200 dark:bg-slate-700" />
          <div className="h-96 rounded-lg bg-slate-200 dark:bg-slate-700" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Exam Details">
      <div className="max-w-6xl animate-fade-in space-y-6">
        <button
          onClick={() => navigate('/faculty/exams')}
          className="inline-flex items-center gap-2 text-slate-600 hover:text-primary dark:text-slate-400 dark:hover:text-accent-cool"
        >
          <ArrowLeft size={18} />
          Back to Exams
        </button>

        <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-slate-900 dark:text-white">{exam.title}</h2>
              <p className="mt-1 text-slate-500 dark:text-slate-400">{exam.subject}</p>
              <div className="mt-4 flex flex-wrap gap-3 text-sm">
                {exam.conduct_mode === 'offline' ? (
                  <span className="rounded-full bg-violet-100 px-3 py-1 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
                    Paper test{exam.exam_date ? ` · ${exam.exam_date}` : ''}
                  </span>
                ) : (
                <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700 dark:bg-slate-700 dark:text-slate-300">
                  {exam.duration_minutes} min
                </span>
                )}
                <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700 dark:bg-slate-700 dark:text-slate-300">
                  {exam.total_marks} marks
                </span>
                <span className={`rounded-full px-3 py-1 ${exam.is_published === 'published' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'}`}>
                  {exam.is_published}
                </span>
                {exam.conduct_mode !== 'offline' && (
                <span className={`rounded-full px-3 py-1 ${exam.submissions_open ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>
                  Submissions: {exam.submissions_open ? 'Open' : 'Closed'}
                </span>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={handlePublishToggle}
                disabled={saving}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-light disabled:opacity-50"
              >
                {exam.is_published === 'published' ? 'Unpublish' : 'Publish'}
              </button>
              {exam.conduct_mode !== 'offline' && (
              <button
                onClick={handleToggleSubmissions}
                disabled={submissionsToggling}
                className={`rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-50 ${
                  exam.submissions_open
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-emerald-600 hover:bg-emerald-700'
                }`}
              >
                {submissionsToggling
                  ? 'Updating...'
                  : exam.submissions_open
                  ? 'Close Submissions'
                  : 'Open Submissions'}
              </button>
              )}
              <button
                onClick={handleDeleteExam}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
              >
                Delete Exam
              </button>
            </div>
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
            <div className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
              <h3 className="mb-3 font-medium text-slate-900 dark:text-white">Question Paper</h3>
              {exam.conduct_mode === 'offline' ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Paper test: the question paper is printed and handed out in class. There is no question file in the system.
                </p>
              ) : exam.pdf_url ? (
                pdfBlobUrl ? (
                  <PdfExamViewer
                    src={pdfBlobUrl}
                    title="Question Paper"
                    className="min-h-[520px]"
                  />
                ) : pdfLoadError ? (
                  <p className="text-sm text-red-500">Failed to load question paper.</p>
                ) : (
                  <p className="text-sm text-slate-500 dark:text-slate-400">Loading question paper...</p>
                )
              ) : (
                <p className="text-sm text-slate-500 dark:text-slate-400">No PDF uploaded for this exam yet.</p>
              )}
            </div>

            <div className="space-y-4">
              <form onSubmit={handleSaveExamDetails} className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
                <h3 className="mb-3 font-medium text-slate-900 dark:text-white">Edit Exam Details</h3>
                <div className="space-y-4">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Title</label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleMetaChange}
                      className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:ring-2 focus:ring-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Subject</label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleMetaChange}
                      className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:ring-2 focus:ring-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                    >
                      {subjectOptions.map((name) => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                    {subjectUnrecognized && (
                      <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                        "{formData.subject}" isn't a recognized subject — pick one from the list before saving.
                      </p>
                    )}
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {exam.conduct_mode === 'offline' ? (
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Exam date</label>
                      <input
                        type="date"
                        name="exam_date"
                        value={formData.exam_date}
                        onChange={handleMetaChange}
                        className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:ring-2 focus:ring-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                      />
                    </div>
                    ) : (
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Duration</label>
                      <input
                        type="number"
                        min="1"
                        max="480"
                        name="duration_minutes"
                        value={formData.duration_minutes}
                        onChange={handleMetaChange}
                        className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:ring-2 focus:ring-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                      />
                    </div>
                    )}
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Total Marks</label>
                      <input
                        type="number"
                        min="1"
                        name="total_marks"
                        value={formData.total_marks}
                        onChange={handleMetaChange}
                        className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:ring-2 focus:ring-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                      />
                    </div>
                  </div>
                  {exam.conduct_mode !== 'offline' && (
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Replace Question Paper PDF</label>
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={(event) => {
                        const file = event.target.files?.[0] || null;
                        const sizeError = checkPdfFile(file, 'Question paper');
                        if (sizeError) {
                          toast.error(sizeError);
                          event.target.value = '';
                          return;
                        }
                        setPdfFile(file);
                      }}
                      className="block w-full text-sm text-slate-600 file:mr-4 file:rounded-md file:border-0 file:bg-slate-100 file:px-4 file:py-2 file:text-slate-700 hover:file:bg-slate-200 dark:text-slate-300 dark:file:bg-slate-700 dark:file:text-slate-200"
                    />
                    <p className="mt-1"><UploadHint /></p>
                  </div>
                  )}
                  <button
                    type="submit"
                    disabled={metaSaving}
                    className="w-full rounded-md bg-primary px-4 py-2.5 text-sm font-medium text-white hover:bg-primary-light disabled:opacity-50"
                  >
                    {metaSaving ? 'Saving...' : 'Save Exam Details'}
                  </button>
                </div>
              </form>

              {exam.conduct_mode !== 'offline' && (<>
              <div className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
                <h3 className="mb-3 font-medium text-slate-900 dark:text-white">Student Exam Link</h3>
                {exam.is_published === 'published' ? (
                  <>
                    <p className="mb-3 break-all rounded-md bg-slate-50 p-3 text-sm text-slate-700 dark:bg-slate-900 dark:text-slate-300">
                      {publicExamUrl}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => handleCopy(publicExamUrl, 'Exam link')}
                        className="inline-flex items-center gap-2 rounded-md bg-slate-100 px-3 py-2 text-sm text-slate-700 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
                      >
                        <Copy size={16} />
                        Copy Link
                      </button>
                      <a
                        href={publicExamUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 rounded-md bg-slate-100 px-3 py-2 text-sm text-slate-700 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
                      >
                        <ExternalLink size={16} />
                        Open
                      </a>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Publish the exam to generate the shareable student login link.
                  </p>
                )}
              </div>

              <div className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
                <h3 className="mb-3 font-medium text-slate-900 dark:text-white">Submitted Papers</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Students can upload answer PDFs after finishing. You can download, mark offline, and upload the corrected paper back here.
                </p>
              </div>
              </>)}
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800">
          <div className="border-b border-slate-200 p-5 dark:border-slate-700">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Assign Students</h3>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Only students assigned here can see and start this exam.
            </p>
          </div>
          <div className="grid gap-4 p-5 lg:grid-cols-2">
            <div>
              <ExamAssignmentPicker
                examId={exam.id}
                assignedStudentIds={assignedStudentIds}
                onAssigned={loadData}
              />
            </div>

            <div>
              <h4 className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">
                Currently Assigned ({assignments.length})
              </h4>
              {assignments.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">No students assigned yet.</p>
              ) : (
                <div className="max-h-96 space-y-2 overflow-y-auto">
                  {assignments.map((assignment) => {
                    const isOffline = exam.conduct_mode === 'offline';
                    const canExtend =
                      !isOffline &&
                      Boolean(assignment.started_at) &&
                      !['submitted', 'evaluated'].includes(assignment.attempt_status) &&
                      exam.submissions_open;
                    const canReopen =
                      !isOffline &&
                      ['submitted', 'evaluated'].includes(assignment.attempt_status) &&
                      exam.submissions_open;
                    return (
                      <div
                        key={assignment.id}
                        className="rounded-md border border-slate-200 px-3 py-2 text-sm dark:border-slate-700"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-slate-900 dark:text-white">{assignment.student_name}</p>
                            <p className="text-slate-500 dark:text-slate-400">{assignment.student_email}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs capitalize text-slate-700 dark:bg-slate-700 dark:text-slate-300">
                              {assignment.status.replace('_', ' ')}
                            </span>
                            {assignment.extended_minutes > 0 && (
                              <span
                                className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                                title={`Deadline extended by ${assignment.extended_minutes} min total`}
                              >
                                <Clock size={11} /> +{assignment.extended_minutes}m
                              </span>
                            )}
                            {assignment.reopened_count > 0 && (
                              <span
                                className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2.5 py-0.5 text-xs text-sky-700 dark:bg-sky-900/30 dark:text-sky-300"
                                title={`Reopened ${assignment.reopened_count} time(s)`}
                              >
                                <RotateCcw size={11} /> Reopened ×{assignment.reopened_count}
                              </span>
                            )}
                            <button
                              onClick={() => handleRevokeAssignment(assignment)}
                              disabled={Boolean(assignment.started_at) || revoking === assignment.id}
                              title={assignment.started_at ? 'Cannot remove: student has already started this exam' : 'Remove assignment'}
                              className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-400 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                        {canExtend && (
                          <div className="mt-2 flex flex-wrap items-center gap-1.5">
                            <span className="text-xs text-slate-400 dark:text-slate-500">Extend:</span>
                            <button
                              onClick={() => handleExtend(assignment, 5)}
                              disabled={extendingId === assignment.id}
                              className="rounded-md bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-200 disabled:opacity-50 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
                            >
                              +5
                            </button>
                            <button
                              onClick={() => handleExtend(assignment, 10)}
                              disabled={extendingId === assignment.id}
                              className="rounded-md bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-200 disabled:opacity-50 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
                            >
                              +10
                            </button>
                            <input
                              type="number"
                              min="1"
                              placeholder="min"
                              value={customMinutes[assignment.id] || ''}
                              onChange={(e) =>
                                setCustomMinutes((prev) => ({ ...prev, [assignment.id]: e.target.value }))
                              }
                              className="w-16 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                            />
                            <button
                              onClick={() => handleExtend(assignment, Number(customMinutes[assignment.id]))}
                              disabled={extendingId === assignment.id || !customMinutes[assignment.id]}
                              className="rounded-md bg-primary/10 px-2 py-1 text-xs font-medium text-primary hover:bg-primary/20 disabled:opacity-50 dark:text-accent-cool"
                            >
                              Apply
                            </button>
                          </div>
                        )}
                        {canReopen && (
                          <div className="mt-2 flex flex-wrap items-center gap-1.5">
                            <span className="text-xs text-slate-400 dark:text-slate-500">Reopen for:</span>
                            <input
                              type="number"
                              min="1"
                              placeholder="5"
                              value={reopenMinutes[assignment.id] || ''}
                              onChange={(e) =>
                                setReopenMinutes((prev) => ({ ...prev, [assignment.id]: e.target.value }))
                              }
                              className="w-16 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                            />
                            <span className="text-xs text-slate-400 dark:text-slate-500">min</span>
                            <button
                              onClick={() => handleReopen(assignment)}
                              disabled={reopeningId === assignment.id}
                              className="inline-flex items-center gap-1 rounded-md bg-sky-100 px-2 py-1 text-xs font-medium text-sky-700 hover:bg-sky-200 disabled:opacity-50 dark:bg-sky-900/30 dark:text-sky-300 dark:hover:bg-sky-900/50"
                            >
                              <RotateCcw size={12} /> {reopeningId === assignment.id ? 'Reopening…' : 'Reopen'}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {exam.conduct_mode === 'offline' && <OfflineEvaluationPanel examId={exam.id} onChanged={loadData} />}

        {exam.conduct_mode !== 'offline' && (
        <div className="rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800">
          <div className="border-b border-slate-200 p-5 dark:border-slate-700">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Submissions</h3>
          </div>

          {submissions.length === 0 ? (
            <div className="p-6 text-sm text-slate-500 dark:text-slate-400">No submissions yet for this exam.</div>
          ) : (
            <div className="space-y-4 p-5">
              {submissions.map((submission) => {
                const resultUrl = `${window.location.origin}/result/${submission.result_token}`;

                return (
                  <div key={submission.attempt_id} className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
                    <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-slate-900 dark:text-white">{submission.student_name || 'Unnamed Student'}</h4>
                          {submission.student_id && (
                            <Link
                              to={`/faculty/students/${submission.student_id}/ledger`}
                              className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-700 px-2 py-0.5 text-xs text-slate-600 dark:text-slate-300 hover:bg-primary/10 hover:text-primary dark:hover:text-accent-cool transition-colors"
                            >
                              <BookOpen size={11} /> Ledger
                            </Link>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          {submission.student_class || 'Class not provided'} | {submission.phone_number || 'Phone not provided'}
                        </p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          Status: {submission.status} | Violations: {submission.violation_count}
                        </p>
                        <p className="text-xs text-slate-400">
                          Result link: {resultUrl}
                        </p>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {(submission.submissions || []).map((s, i) => (
                          <button
                            key={s.id}
                            onClick={() => handleAuthDownload(
                              () => facultyExamApi.downloadSubmission(s.id),
                              `submission_${i + 1}.pdf`
                            )}
                            className="inline-flex items-center gap-1.5 rounded-md bg-slate-100 px-3 py-2 text-sm text-slate-700 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
                          >
                            <Download size={14} />
                            Paper {i + 1}{s.is_late ? ' (late)' : ''}
                          </button>
                        ))}
                        {submission.has_corrected_paper && (
                          <button
                            onClick={() => handleAuthDownload(
                              () => facultyExamApi.downloadCorrectedPaper(submission.attempt_id),
                              'corrected_paper.pdf'
                            )}
                            className="inline-flex items-center gap-1.5 rounded-md bg-emerald-100 px-3 py-2 text-sm text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300"
                          >
                            <Download size={14} />
                            Corrected Paper
                          </button>
                        )}
                        <button
                          onClick={() => handleCopy(resultUrl, 'Result link')}
                          className="rounded-md bg-slate-100 px-3 py-2 text-sm text-slate-700 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
                        >
                          Copy Result Link
                        </button>
                        <Link
                          to={`/faculty/exams/${examId}/evaluations/${submission.attempt_id}`}
                          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-white hover:bg-primary-light"
                        >
                          <ClipboardCheck size={14} />
                          {submission.total_score != null ? 'Re-evaluate' : 'Evaluate'}
                        </Link>
                      </div>
                    </div>

                    {submission.violation_details && Object.keys(submission.violation_details).length > 0 && (
                      <div className="mt-3 text-xs text-amber-700 dark:text-amber-300">
                        {Object.entries(submission.violation_details).map(([key, value]) => `${key}: ${value}`).join(' | ')}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        )}
      </div>
    </DashboardLayout>
  );
}
